import React, { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Formik, FormikProps } from "formik";
import { Form, Input, SubmitButton } from "formik-antd";
import { Row, Col } from "antd";
import { toast } from "sonner";
import { userService } from "/app/src/services";
import { userResetSchema } from "/app/src/schemas";
import { useAuthState } from "/app/src/contexts/authentication";
import { useTranslation } from "react-i18next";

interface FormValues {
  search: string;
}

/**
 * Renders the forgot password form and handles password reset functionality.
 *
 * @returns {JSX.Element} The forgot password form component.
 */
export default function ForgotForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();
  // redirect to home if already logged in
  useEffect(() => {
    if (user?.id) {
      navigate("/");
    }
  }, [user, navigate]);

  /**
   * Displays a success toast after sending the password reset email.
   *
   * @returns {void}
   */
  const success = useCallback(() => {
    toast.success(t("translation:password_reset_email"));
  }, [t]);

  /**
   * Handles the password reset process by calling the reset service and updating form submission state.
   *
   * @param {Object} param0 - The parameters object.
   * @param {string} param0.search - The search parameter used for password reset.
   * @param {Object} actions - Formik actions for controlling form submission state.
   * @returns {void}
   */
  const resetPasswordHandler = useCallback(
    ({ search }, actions) => {
      const path = `${window.location.protocol}//${window.location.host}`;
      userService.reset({ search, path }).finally(() => {
        success();
        actions.setSubmitting(false);
      });
    },
    [success],
  );

  /**
   * Renders the reset password form.
   *
   * @param {Object} props - The Formik props.
   * @param {Object} props.errors - Form errors.
   * @param {Object} props.touched - Touched fields information.
   * @param {boolean} props.isValid - Form validation status.
   * @param {boolean} props.dirty - Indicates if the form has been modified.
   * @returns {JSX.Element} The JSX form element.
   */
  const ResetForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ errors, touched, isValid, dirty }) => (
        <Form>
          <Form.Item name="search">
            <Input
              suffix
              name="search"
              placeholder={t("translation:reset_email_tip")}
              className={
                errors.search && touched.search ? "input-error" : undefined
              }
              size="large"
            />
          </Form.Item>
          <SubmitButton
            type="primary"
            block
            size="large"
            disabled={!(dirty && isValid)}
          >
            {t("translation:send_reset_email")}
          </SubmitButton>
        </Form>
      ),
      [t],
    );

  return (
    <div className="login">
      <Helmet>
        <title>
          {t("translation:forgot")} {t("translation:your_password")} - ItemPath
        </title>
      </Helmet>
      <Row>
        <Col
          xs={{ span: 24 }}
          sm={{ span: 20, offset: 2 }}
          lg={{ span: 8, offset: 8 }}
        >
          <div className="box mt-4">
            <h3 style={{ textAlign: "center", margin: "20px 0 30px" }}>
              {t("translation:reset_link_sent")}
            </h3>
            <Formik
              component={ResetForm}
              initialValues={{
                search: "",
              }}
              validationSchema={userResetSchema}
              onSubmit={resetPasswordHandler}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
}
